import {
  BLACK,
  BODY_TEXT,
  BRIGHT_BLUE,
  DARK_GREY,
  DIVIDER,
  GREY,
  SCOUT_BLUE,
  SCOUT_TEAL,
  WHITE
} from '../../ThemeContext/constants';
import { theme } from '../../ThemeContext/ThemeObject';

import type { SxProps, Theme } from '@mui/material';

export const baseButtonStyles = {
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontWeight: 'bold',
  textTransform: 'none',
  borderRadius: '6px',
  boxShadow: 'none'
} satisfies SxProps<Theme>;

export const basePrimaryButtonStyles = {
  ...baseButtonStyles,
  color: WHITE,
  backgroundColor: SCOUT_TEAL,
  '&:hover': {
    backgroundColor: '#4da8a4',
    boxShadow: 'none'
  }
} satisfies SxProps<Theme>;

export const baseSecondaryButtonStyles = {
  ...baseButtonStyles,
  color: GREY,
  backgroundColor: 'rgba(147, 147, 147, 0.12)',
  '&:hover': {
    backgroundColor: 'rgba(147, 147, 147, 0.24)',
    boxShadow: 'none'
  }
} satisfies SxProps<Theme>;

export const tableSearchButtonStyles = {
  ...baseButtonStyles,
  color: '#4E4EC7',
  backgroundColor: 'rgba(78, 78, 199, 0.18)',
  height: '60px',
  '&:hover': {
    backgroundColor: 'rgba(78, 78, 199, 0.14)',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none',
    backgroundColor: 'rgba(78, 78, 199, 0.14)'
  },
  '&:disabled': {
    backgroundColor: `${DARK_GREY}1A`
  }
} satisfies SxProps<Theme>;

export const felixPrimaryButtonStyles = {
  ...baseButtonStyles,
  color: 'white',
  backgroundColor: BRIGHT_BLUE,
  height: '60px',
  minWidth: '72px',
  borderRadius: '32px',
  whiteSpace: 'nowrap',
  padding: '0 24px',
  transition: 'background-color 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: '#5A6BC7',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none',
    backgroundColor: '#5A6BC7'
  },
  '&:disabled': {
    backgroundColor: `${DARK_GREY}1A`
  }
} satisfies SxProps<Theme>;

export const felixSecondaryButtonStyles = {
  ...baseButtonStyles,
  color: '#4E4EC7',
  backgroundColor: 'rgba(78, 78, 199, 0.18)',
  height: '60px',
  minWidth: '72px',
  borderRadius: '32px',
  whiteSpace: 'nowrap',
  padding: '0 24px',
  transition: 'background-color 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(78, 78, 199, 0.3)',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none',
    backgroundColor: 'rgba(78, 78, 199, 0.3)'
  },
  '&:disabled': {
    backgroundColor: `${DARK_GREY}1A`
  }
} satisfies SxProps<Theme>;

export const felixBorderButtonStyles = {
  ...baseButtonStyles,
  color: BRIGHT_BLUE,
  backgroundColor: 'white',
  border: `2px solid ${BRIGHT_BLUE}`,
  borderRadius: '32px',
  transition:
    'background-color 0.3s ease-in-out, color 0.3s ease-in-out, border-color 0.3s ease-in-out',
  '&:hover': {
    color: '#5A6BC7',
    backgroundColor: '#5A6BC71A',
    borderColor: '#5A6BC7',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none',
    color: '#5A6BC7',
    backgroundColor: '#5A6BC71A',
    borderColor: '#5A6BC7'
  },
  '&:disabled': {
    backgroundColor: `${DARK_GREY}1A`
  }
} satisfies SxProps<Theme>;

export const felixTextButtonStyles = {
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '18px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  padding: '12px 24px',
  borderRadius: '28px',
  background: 'transparent',
  color: '#858585',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    background: 'rgba(133, 133, 133, 0.08)',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none'
  }
} satisfies SxProps<Theme>;

export const felixTextFieldStyles = {
  '& .MuiInputBase-root': { fontFamily: 'Source Sans Pro, sans-serif' },
  '& .MuiInputBase-input': {
    fontSize: 14,
    fontFamily: 'Source Sans Pro, sans-serif',
    color: BODY_TEXT
  },
  '& .MuiInputBase-input::placeholder': {
    fontFamily: 'Source Sans Pro, sans-serif'
  },
  '& .MuiOutlinedInput-root': { borderRadius: '24px' },
  '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.3s ease' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: BRIGHT_BLUE,
    borderWidth: '1px'
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BRIGHT_BLUE,
    borderWidth: '1px'
  }
} satisfies SxProps<Theme>;

export const toggleScreenButtonStyles = {
  ...baseSecondaryButtonStyles,
  color: DARK_GREY,
  backgroundColor: '#F5F5F5'
} satisfies SxProps<Theme>;

export const statusClampCommonStyles = {
  display: '-webkit-box',
  position: 'relative',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  minHeight: '35px',
  maxHeight: '53px',
  textAlign: 'left'
} satisfies SxProps<Theme>;

export const statusClampAfterStyles = {
  content: '"▾"',
  fontSize: '13px',
  color: '#CCCCCC',
  position: 'absolute',
  right: '6px',
  lineHeight: '0',
  top: '50%'
} satisfies SxProps<Theme>;

export const statusClampDropdownStyles = {
  visibility: 'hidden',
  minHeight: '35px',
  textAlign: 'left',
  position: 'absolute',
  top: '-1px',
  left: '-1px',
  zIndex: '2',
  background: WHITE,
  borderRadius: '6px',
  border: '1px solid #CCCCCC'
} satisfies SxProps<Theme>;

export const statusClampDropdownAfterStyles = {
  content: '"▾"',
  fontSize: '13px',
  color: '#CCCCCC',
  position: 'absolute',
  right: '6px',
  lineHeight: '0'
} satisfies SxProps<Theme>;

export const statusClampHoverStyles = {
  overflow: 'visible',
  WebkitLineClamp: 'unset',
  '& .status-dropdown-full': {
    visibility: 'visible'
  }
} satisfies SxProps<Theme>;

export const scrollShadowAfterStyles = {
  content: '""',
  background: '#aaaaaa',
  width: '2px',
  height: '100%',
  filter: 'blur(2px)',
  position: 'absolute',
  top: '0',
  opacity: '0%',
  transition: 'opacity 0.17s linear'
} satisfies SxProps<Theme>;

export const scrollShadowBeforeStyles = {
  content: '""',
  top: '0',
  width: '6px',
  height: '100%',
  position: 'absolute',
  background: WHITE,
  zIndex: '2'
} satisfies SxProps<Theme>;

export const candidateSearchCommonStyles = {
  fontFamily: 'Source Sans Pro',
  fontSize: '16px',
  fontWeight: 'regular',
  color: GREY,
  borderRadius: '6px',
  width: '320px',
  '& .MuiOutlinedInput-root': {
    paddingLeft: '3px'
  },
  '& legend': {
    display: 'none'
  },
  '& svg': {
    fontSize: '22px',
    color: GREY,
    transform: 'translate(-6px, -1px)'
  },
  '& input': {
    padding: '8px 9px 12px 0px',
    color: BLACK
  }
} satisfies SxProps<Theme>;

export const centeredModalPositionStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
} satisfies SxProps<Theme>;

export const centeredModalSurfaceStyles = {
  ...centeredModalPositionStyles,
  borderRadius: '6px',
  boxShadow: '0px 2px 14px #305A6F29',
  background: WHITE
} satisfies SxProps<Theme>;

export const actionsModalReducedPaddingCommonStyles = {
  ...centeredModalSurfaceStyles,
  display: 'flex',
  flexDirection: 'column',
  padding: '60px',
  paddingLeft: '48px',
  width: '100%',
  maxWidth: '900px',
  height: '84vh',
  '& h3': {
    color: GREY,
    fontSize: '19px',
    fontWeight: 'Bold',
    marginBottom: '30px'
  }
} satisfies SxProps<Theme>;

export const filterPopoverHeaderCommonStyles = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '14px',
  backgroundColor: 'white',
  position: 'sticky',
  zIndex: 99,
  paddingTop: '26px',
  paddingBottom: '2px',
  boxShadow: `0px 6px 10px 7px ${WHITE}`
} satisfies SxProps<Theme>;

export const actionsMenuPaperCommonStyles = {
  display: 'flex',
  flexDirection: 'column',
  rowGap: '10px',
  padding: '14px 0px 14px 20px',
  borderRadius: '6px'
} satisfies SxProps<Theme>;

export const actionsMenuSpanCommonStyles = {
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontWeight: 'bold',
  color: DARK_GREY,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main
  }
} satisfies SxProps<Theme>;

export const switchLabelsCommonStyles = {
  justifyContent: 'space-between',
  width: '100%',
  '& .MuiFormControlLabel-label': {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontWeight: 'bold',
    color: SCOUT_BLUE
  },
  '& .MuiSwitch-track': {
    backgroundColor: 'rgba(0,0,0,0.38)',
    opacity: '1'
  },
  '& .Mui-checked+.MuiSwitch-track': {
    backgroundColor: 'rgb(91, 196, 191, 0.17) !important',
    opacity: '1 !important'
  },
  '& .Mui-checked': {
    color: 'rgb(91, 196, 191, 0.5) !important'
  }
} satisfies SxProps<Theme>;

const arrowBaseStyles = {
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '65px',
  bottom: '370px',
  background: '#eeeeee',
  opacity: '0.9',
  cursor: 'pointer'
} satisfies SxProps<Theme>;

export const rightArrowStyles = {
  ...arrowBaseStyles,
  borderRadius: '10px 0px 0px 10px'
} satisfies SxProps<Theme>;

export const leftArrowStyles = {
  ...arrowBaseStyles,
  borderRadius: '0px 10px 10px 0px'
} satisfies SxProps<Theme>;

export const applyButtonStyles = {
  ...baseButtonStyles,
  color: WHITE,
  backgroundColor: SCOUT_TEAL,
  padding: '5px 23px',
  '&:hover': {
    backgroundColor: '#4da8a4',
    boxShadow: 'none'
  }
} satisfies SxProps<Theme>;

export const actionButtonStyles = {
  ...baseButtonStyles,
  fontWeight: '600',
  color: DARK_GREY,
  backgroundColor: `${GREY}1F`,
  padding: '10px 24px',
  border: 'none',
  '&:hover': {
    backgroundColor: `${GREY}35`,
    border: 'none'
  },
  '&:focus': {
    outline: 'none'
  }
} satisfies SxProps<Theme>;

export const filterPopoverContentStyles = {
  width: '272px',
  maxHeight: '500px',
  boxShadow: '0px 2px 14px rgba(48,90,111,0.16)',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  position: 'relative',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
} satisfies SxProps<Theme>;

export const selectedApplicantsTableStyles = {
  tableLayout: 'auto',
  width: 'unset',
  border: 'none',
  th: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    '& .Mui-active': {
      color: theme.palette.primary.main
    }
  },
  tr: {
    fontSize: '16px',
    border: 'none'
  }
} satisfies SxProps<Theme>;

export const selectedApplicantsTableRowStyles = {
  fontFamily: 'Source Sans Pro, sans-serif',
  border: 'none',
  backgroundColor: WHITE,
  textTransform: 'none'
} satisfies SxProps<Theme>;

export const modalConfirmationButtonCommonStyles = {
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontWeight: 'bold',
  textTransform: 'none',
  color: WHITE,
  backgroundColor: SCOUT_TEAL,
  borderRadius: '6px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#4da8a4',
    boxShadow: 'none'
  }
} satisfies SxProps<Theme>;

export const selectedTooltipCommonStyles = {
  display: 'flex',
  alignItems: 'center',
  visibility: 'hidden',
  bottom: '40px',
  position: 'fixed',
  zIndex: 99,
  backgroundColor: '#F7FCFC',
  borderRadius: '8px',
  transform: 'translateX(20px)'
} satisfies SxProps<Theme>;

export const rowSelectContainerCommonStyles = {
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  marginTop: '5px',
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontWeight: 'bold',
  color: DARK_GREY,
  '& span': {
    marginRight: '12px',
    paddingBottom: '6px'
  }
} satisfies SxProps<Theme>;

export const checkboxContainerStyles = {
  fontFamily: 'Source Sans Pro',
  fontSize: '14px',
  fontWeight: 'bold',
  color: SCOUT_BLUE,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
} satisfies SxProps<Theme>;

export const weightSliderStyles = {
  width: '100%',
  '& .MuiSlider-thumb': {
    width: '18px',
    height: '18px'
  },
  '& .MuiSlider-rail': {
    height: '12px'
  },
  '& .MuiSlider-track ': {
    height: '12px'
  }
} satisfies SxProps<Theme>;

export const centerAlignStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%'
} satisfies SxProps<Theme>;

export const redIconButtonStyles = {
  color: '#CF726F !important',
  '&:hover': {
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none'
  }
} satisfies SxProps<Theme>;

export const tooltipStyles = {
  fontFamily: 'Source Sans Pro, sans-serif',
  fontSize: '14px'
} satisfies SxProps<Theme>;

export const felixAutocompleteDropdownStyles = {
  borderRadius: '8px',
  boxShadow: '0px 4px 16px #4E4EC71A',
  marginTop: '8px',
  fontFamily: 'Source Sans Pro, sans-serif'
} satisfies SxProps<Theme>;

export const compactPrimaryButtonStyles = {
  ...basePrimaryButtonStyles,
  padding: '5px 0px'
} satisfies SxProps<Theme>;

export const compactPrimaryButtonFixedWidthStyles = {
  ...compactPrimaryButtonStyles,
  width: '120px'
} satisfies SxProps<Theme>;

export const compactPrimaryButtonNarrowStyles = {
  ...compactPrimaryButtonStyles,
  width: '90px',
  fontSize: '12px',
  fontWeight: '600'
} satisfies SxProps<Theme>;

export const modalPrimaryButtonPaddedStyles = {
  ...modalConfirmationButtonCommonStyles,
  padding: '8px 14px'
} satisfies SxProps<Theme>;

export const modalPrimaryButtonWideStyles = {
  ...modalConfirmationButtonCommonStyles,
  padding: '8px 20px',
  minWidth: '110px',
  borderRadius: '8px'
} satisfies SxProps<Theme>;

export const tealGhostButtonStyles = {
  ...baseButtonStyles,
  color: SCOUT_TEAL,
  backgroundColor: `${SCOUT_TEAL}1F`,
  padding: '5px 0px',
  '&:hover': {
    backgroundColor: `${SCOUT_TEAL}35`,
    boxShadow: 'none'
  }
} satisfies SxProps<Theme>;

export const tealGhostButtonWideStyles = {
  ...tealGhostButtonStyles,
  width: '220px',
  fontWeight: 'bold'
} satisfies SxProps<Theme>;

export const emptyStateTextStyles = {
  fontFamily: 'Source Sans Pro, sans-serif',
  color: '#989898',
  fontSize: '18px',
  fontWeight: 'bold'
} satisfies SxProps<Theme>;

export const emptyStateTextCenteredStyles = {
  ...emptyStateTextStyles,
  margin: '32px 0px',
  textAlign: 'center'
} satisfies SxProps<Theme>;

export const emptyStateTextCompactStyles = {
  ...emptyStateTextStyles,
  marginTop: '8px'
} satisfies SxProps<Theme>;

export const subPageHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0px 16px',
  '& h2': {
    fontWeight: 'bold',
    fontSize: '20px',
    lineHeight: '1.2em',
    color: DARK_GREY,
    marginTop: '0',
    '@media (max-width: 1450px)': {
      fontSize: '20px'
    },
    '@media (max-width: 1260px)': {
      fontSize: '14px'
    }
  }
} satisfies SxProps<Theme>;

export const closeIconCompactStyles = {
  position: 'absolute',
  top: '16px',
  right: '16px',
  cursor: 'pointer',
  color: DIVIDER
} satisfies SxProps<Theme>;

export const createZoomSliderStyles = (
  color: string
): {
  color: string;
  '& .MuiSlider-thumb': {
    backgroundColor: string;
    width: string;
    height: string;
  };
  '& .MuiSlider-rail': {
    color: string;
    height: string;
  };
  '& .MuiSlider-track ': {
    height: string;
  };
} =>
  ({
    color,
    '& .MuiSlider-thumb': {
      backgroundColor: color,
      width: '16px',
      height: '16px'
    },
    '& .MuiSlider-rail': {
      color,
      height: '8px'
    },
    '& .MuiSlider-track ': {
      height: '8px'
    }
  }) satisfies SxProps<Theme>;
